import { useEffect, useReducer } from 'react';

// ============================================================================
// Theme (light / dark / system) — BBM Context for claude/theme.md.
//
// Source of truth at runtime is `pref` here + localStorage('bbm.theme') so
// the choice applies before login and before React mounts (index.html's
// boot script reads the same key). The per-user copy lives in
// user_profiles.theme_pref (sql/091): AuthContext calls setThemePref() with
// it after the profile loads and saves changes back. If that column isn't
// there yet the save fails quietly and the device-local copy still works.
//
// Rendering: html.dark + src/theme.css. Components almost never need to
// know the theme — the slate scale and the two-meaning utilities re-map
// themselves. The exception is INLINE colors (status pills painted from
// status.bg): use statusTint() for those.
// ============================================================================

export const THEME_KEY = 'bbm.theme';
export const THEME_OPTIONS = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
];
const VALID = new Set(THEME_OPTIONS.map(o => o.value));

const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
let pref = 'system';
try { const v = localStorage.getItem(THEME_KEY); if (VALID.has(v)) pref = v; } catch (_) { /* private mode */ }

const listeners = new Set();
const notify = () => listeners.forEach(fn => { try { fn(); } catch (_) {} });

export const getThemePref = () => pref;
export const resolvedIsDark = (p = pref) => p === 'dark' || (p === 'system' && !!(mq && mq.matches));

export function applyTheme() {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', resolvedIsDark());
    notify();
}

export function setThemePref(next) {
    if (!VALID.has(next) || next === pref) { applyTheme(); return; }
    pref = next;
    try { localStorage.setItem(THEME_KEY, next); } catch (_) {}
    applyTheme();
}

if (mq) {
    const onChange = () => { if (pref === 'system') applyTheme(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange); else if (mq.addListener) mq.addListener(onChange);
}

// React hook: re-renders on any theme change. Returns the preference, the
// resolved boolean, and a setter (device-local only — AuthContext wraps it
// with the profile save; use useAuth().saveThemePref from UI).
export function useTheme() {
    const [, force] = useReducer(x => x + 1, 0);
    useEffect(() => { listeners.add(force); return () => listeners.delete(force); }, []);
    return { pref, isDark: resolvedIsDark(), setPref: setThemePref };
}

// Inline status colors: statuses carry a solid `color` and a pale `bg`
// tuned for white. On dark the pale tint glows, so paint a translucent
// wash of the solid color instead (18% — same as the Theme Showroom).
export const hexAlpha = (hex, a) => {
    if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex)) return hex;
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};
export const statusTint = (color, lightBg, isDark = resolvedIsDark()) =>
    isDark ? hexAlpha(color, 0.18) : (lightBg || hexAlpha(color, 0.12));

export default { getThemePref, setThemePref, resolvedIsDark, applyTheme, useTheme, statusTint, hexAlpha, THEME_OPTIONS };
